import styled from "styled-components";
import CarouselBase, { CarouselBaseMethods } from "../components/CarouselBase";
import { CarouselItemThemeAttributes } from "../attributes/CarouselItemTheme";
import { CarouselSettingsAttributes } from "../attributes/CarouselSettings";
import { Attribute, AttributeGroup, AttributeProperty, ComponentProps } from "react-builder";
import { StateIcon, StateIconAttributes, SwitchActionButtonAttributes } from "react-builder";
import { SwitchActionButton, ConfigsProvider, ContainerAttributes } from "react-builder";
import { useEffect, useRef, useState } from "react";

export class CarouselScrollButtonAttributes extends Attribute {
  @AttributeProperty({ title: "Icon", type: "Object", disable: ["state"] })
  icon: StateIconAttributes = new StateIconAttributes();
  @AttributeProperty({ title: "Button", type: "Object", cssTarget: "", disable: ["state"] })
  button: SwitchActionButtonAttributes = new SwitchActionButtonAttributes();
}

@AttributeGroup({ title: "Carousel", blockName: "Carousel", icon: "carousel" })
export class CarouselAttributes extends Attribute {
  @AttributeProperty({ type: "Object" })
  carouselSettings: CarouselSettingsAttributes = new CarouselSettingsAttributes();
  @AttributeProperty({ type: "Object", cssTarget: "" })
  container: ContainerAttributes = new ContainerAttributes();
  @AttributeProperty({ title: "Right Button", type: "Object" })
  rightStateIconConfiguration: CarouselScrollButtonAttributes = new CarouselScrollButtonAttributes();
  @AttributeProperty({ title: "Left Button", type: "Object" })
  leftStateIconConfiguration: CarouselScrollButtonAttributes = new CarouselScrollButtonAttributes();
  @AttributeProperty({ type: "Object", cssTarget: " .carousel-item" })
  itemTheme: CarouselItemThemeAttributes = new CarouselItemThemeAttributes();
}

const CarouselTheme = styled.div(
  (props: { attributes: CarouselAttributes }) => `

    ${props.attributes.toCSS()}

`
);

export function Carousel(props: ComponentProps<CarouselAttributes>) {
  const carouselMethodsRef = useRef<CarouselBaseMethods | null>(null);
  const [hasRight, setHasRight] = useState(true);
  const [hasLeft, setHasLeft] = useState(false);
  const [sliderConfigJson, setSliderConfigJson] = useState({
    slider: {
      hasMoreToRight: hasRight,
      hasMoreToLeft: hasLeft,
    },
  });

  //   update context provider config with has right and has left values
  useEffect(() => {
    setSliderConfigJson({
      slider: { hasMoreToRight: hasRight, hasMoreToLeft: hasLeft },
    });
  }, [hasRight, hasLeft]);

  const onClickLeft = () => {
    if (carouselMethodsRef.current) {
      carouselMethodsRef.current.handleMove("left");
    }
  };
  const onClickRight = () => {
    if (carouselMethodsRef.current) {
      carouselMethodsRef.current.handleMove("right");
    }
  };

  // update state and context state, triggered by carousel base
  const handleUpdateHasRight = (hasRightUpdate: boolean): void => {
    setHasRight(hasRightUpdate);
  };
  const handleUpdateHasLeft = (hasLeftUpdate: boolean): void => {
    setHasLeft(hasLeftUpdate);
  };

  return (
    <ConfigsProvider configJson={{ ...sliderConfigJson }}>
      <CarouselTheme {...props} attributes={props.attributes}>
        <CarouselBase
          settings={props.attributes.carouselSettings}
          ref={carouselMethodsRef}
          onUpdateHasRight={(hasRight) => {
            handleUpdateHasRight(hasRight);
          }}
          onUpdateHasLeft={(hasLeft) => {
            handleUpdateHasLeft(hasLeft);
          }}
        >
          {props.children}
        </CarouselBase>
        <SwitchActionButton
          attributes={props.attributes.leftStateIconConfiguration.button.fromJSON({
            state: "{slider.hasMoreToLeft}",
          })}
          onClick={onClickLeft}
        >
          <StateIcon
            attributes={props.attributes.leftStateIconConfiguration.icon.fromJSON({
              state: "{slider.hasMoreToLeft}",
            })}
            className={`carousel-button-left ${!hasLeft ? "button-limit" : ""}`}
          ></StateIcon>
        </SwitchActionButton>
        <SwitchActionButton
          attributes={props.attributes.rightStateIconConfiguration.button.fromJSON({
            state: "{slider.hasMoreToRight}",
          })}
          onClick={onClickRight}
        >
          <StateIcon
            attributes={props.attributes.rightStateIconConfiguration.icon.fromJSON({
              state: "{slider.hasMoreToRight}",
            })}
            className={`carousel-button-right ${!hasRight ? "button-limit" : ""}`}
          ></StateIcon>
        </SwitchActionButton>
      </CarouselTheme>
    </ConfigsProvider>
  );
}

Carousel.prototype.Props = CarouselAttributes;

export default Carousel;
